import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Stepper,
  Step,
  StepLabel,
  Grid,
  Card,
  CardContent,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  FormLabel,
  Alert,
  CircularProgress,
  Select,
  MenuItem,
  InputLabel,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  ArrowForward as ArrowForwardIcon,
  Dashboard as DashboardIcon,
  ViewModule as GridIcon,
  Description as PageIcon,
  Chat as AIChatIcon,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';

import { useCreateCanvas } from '../../data/canvas/useCanvases';
import { CanvasApi } from '../../data/canvas/canvasApi';
import {
  useCanvasSlugValidation,
  normalizeSlug,
  generateSlugFromName,
} from '../../data/canvas/useSlugValidation';
import { useDatastores } from '../../data/canvas/useDatastores';
import { LayoutType } from '../../types/canvas.types';
import { PageTitle } from '../PageTitle';
import { BrowserTabTitle } from '../BrowserTabTitle';

interface CanvasData {
  name: string;
  slug: string;
  description: string;
  datastore_id: string;
}

interface PageData {
  title: string;
  slug: string;
  layout_type: LayoutType;
}

const steps = ['Canvas Details', 'First Page', 'Review'];

const layoutOptions = [
  {
    value: 'grid' as LayoutType,
    label: 'Grid Layout',
    description: 'Drag-and-drop widgets freely.',
    icon: <GridIcon />,
  },
  {
    value: 'page' as LayoutType,
    label: 'Page Layout',
    description: 'Structured, document-like content.',
    icon: <PageIcon />,
  },
  {
    value: 'ai_chat' as LayoutType,
    label: 'AI Chat Layout',
    description: 'Interactive AI conversation interface.',
    icon: <AIChatIcon />,
  },
];

export const CanvasWizard: React.FC = () => {
  const [activeStep, setActiveStep] = useState(0);
  const [canvasData, setCanvasData] = useState<CanvasData>({
    name: '',
    slug: '',
    description: '',
    datastore_id: '',
  });
  const [pageData, setPageData] = useState<PageData>({
    title: 'Dashboard',
    slug: 'dashboard',
    layout_type: 'grid',
  });

  const { accountId } = useParams();
  const navigate = useNavigate();

  const createCanvasMutation = useCreateCanvas(accountId);
  const { data: datastores, isLoading: datastoresLoading } = useDatastores(
    accountId || '',
  );

  // Slug validation
  const canvasSlugValidation = useCanvasSlugValidation(
    accountId || '',
    canvasData.slug,
    canvasData.slug.length > 0,
  );

  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleCanvasChange = (field: keyof CanvasData, value: string) => {
    setCanvasData((prev) => ({ ...prev, [field]: value }));

    // Auto-generate slug from name
    if (field === 'name') {
      const slug = generateSlugFromName(value);
      setCanvasData((prev) => ({ ...prev, slug }));
    }

    // Normalize slug when manually edited
    if (field === 'slug') {
      const normalizedSlug = normalizeSlug(value);
      if (normalizedSlug !== value) {
        setCanvasData((prev) => ({ ...prev, slug: normalizedSlug }));
      }
    }
  };

  const handlePageChange = (
    field: keyof PageData,
    value: string | LayoutType,
  ) => {
    setPageData((prev) => ({ ...prev, [field]: value }));

    // Auto-generate slug from title
    if (field === 'title') {
      const slug = generateSlugFromName(value as string);
      setPageData((prev) => ({ ...prev, slug }));
    }
  };

  const handleCreate = async () => {
    try {
      // Create canvas
      const canvas = await createCanvasMutation.mutateAsync({
        name: canvasData.name,
        slug: canvasData.slug,
        datastore_id: canvasData.datastore_id || undefined,
        settings: { description: canvasData.description },
      });

      // Create first page using the Canvas ID from the response
      const pageResponse = await CanvasApi.createPage(accountId!, canvas.id, {
        title: pageData.title,
        slug: pageData.slug,
        layout_type: pageData.layout_type,
        order: 0,
      }).run();

      // Navigate to the new canvas
      navigate(`/${accountId}/canvases/${canvas.slug}`);
    } catch (error: any) {
      console.error('Error creating canvas:', error);

      // Handle slug conflict errors
      if (error?.response?.status === 409) {
        const errorData = error.response.data;
        if (errorData?.code === 'SLUG_EXISTS') {
          // Force refresh the slug validation
          canvasSlugValidation.refetch();
        }
      }
    }
  };

  const handleCancel = () => {
    navigate(`/${accountId}/canvases`);
  };

  const isStepValid = (step: number) => {
    switch (step) {
      case 0:
        const hasCanvasData =
          canvasData.name.trim() !== '' &&
          canvasData.slug.trim() !== '' &&
          canvasData.datastore_id.trim() !== '';
        const slugIsAvailable =
          !canvasSlugValidation.data || canvasSlugValidation.data.available;
        return (
          hasCanvasData && slugIsAvailable && !canvasSlugValidation.isLoading
        );
      case 1:
        return pageData.title.trim() !== '' && pageData.slug.trim() !== '';
      default:
        return true;
    }
  };

  const renderStepContent = (step: number) => {
    switch (step) {
      case 0:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Canvas Information
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Canvas Name"
                  value={canvasData.name}
                  onChange={(e) => handleCanvasChange('name', e.target.value)}
                  placeholder="My Dashboard"
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="URL Slug"
                  value={canvasData.slug}
                  onChange={(e) => handleCanvasChange('slug', e.target.value)}
                  placeholder="my-dashboard"
                  helperText={
                    canvasSlugValidation.isLoading
                      ? 'Checking availability...'
                      : canvasSlugValidation.data &&
                          !canvasSlugValidation.data.available
                        ? 'This slug is already taken'
                        : 'This will be used in the URL'
                  }
                  error={
                    canvasSlugValidation.data &&
                    !canvasSlugValidation.data.available
                  }
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth required>
                  <InputLabel>Default Datastore</InputLabel>
                  <Select
                    value={canvasData.datastore_id}
                    onChange={(e) =>
                      handleCanvasChange('datastore_id', e.target.value)
                    }
                    label="Default Datastore"
                  >
                    {datastoresLoading ? (
                      <MenuItem disabled>
                        <CircularProgress size={20} sx={{ mr: 1 }} />
                        Loading datastores...
                      </MenuItem>
                    ) : datastores && datastores.length > 0 ? (
                      datastores.map((ds) => (
                        <MenuItem key={ds.id} value={ds.id}>
                          {ds.name} ({ds.type_name})
                        </MenuItem>
                      ))
                    ) : (
                      <MenuItem disabled>No datastores available</MenuItem>
                    )}
                  </Select>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ mt: 0.5 }}
                  >
                    Widgets will use this datastore by default unless overridden
                  </Typography>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Description"
                  value={canvasData.description}
                  onChange={(e) =>
                    handleCanvasChange('description', e.target.value)
                  }
                  placeholder="A brief description of this canvas"
                  multiline
                  rows={4}
                />
              </Grid>
            </Grid>
          </Box>
        );
      case 1:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              First Page Details
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Page Title"
                  value={pageData.title}
                  onChange={(e) => handlePageChange('title', e.target.value)}
                  placeholder="Overview"
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="URL Slug"
                  value={pageData.slug}
                  onChange={(e) => handlePageChange('slug', e.target.value)}
                  placeholder="overview"
                  helperText="This will be used in the URL"
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <FormControl component="fieldset" margin="normal">
                  <FormLabel component="legend">Layout Type</FormLabel>
                  <RadioGroup
                    row
                    aria-label="layout-type"
                    name="layout-type"
                    value={pageData.layout_type}
                    onChange={(e) =>
                      handlePageChange(
                        'layout_type',
                        e.target.value as LayoutType,
                      )
                    }
                  >
                    {layoutOptions.map((option) => (
                      <FormControlLabel
                        key={option.value}
                        value={option.value}
                        control={<Radio />}
                        label={
                          <Card
                            variant="outlined"
                            sx={{ display: 'flex', alignItems: 'center', p: 1 }}
                          >
                            {option.icon}
                            <CardContent
                              sx={{ p: 0, ml: 1, '&:last-child': { pb: 0 } }}
                            >
                              <Typography variant="subtitle2">
                                {option.label}
                              </Typography>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                {option.description}
                              </Typography>
                            </CardContent>
                          </Card>
                        }
                      />
                    ))}
                  </RadioGroup>
                </FormControl>
              </Grid>
            </Grid>
          </Box>
        );
      case 2:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Review your Canvas
            </Typography>
            <Typography variant="subtitle1">
              Canvas Name: {canvasData.name}
            </Typography>
            <Typography variant="body2">Slug: {canvasData.slug}</Typography>
            <Typography variant="body2">
              Description: {canvasData.description}
            </Typography>
            <Typography variant="subtitle1" sx={{ mt: 2 }}>
              First Page Title: {pageData.title}
            </Typography>
            <Typography variant="body2">Slug: {pageData.slug}</Typography>
            <Typography variant="body2">
              Layout Type: {pageData.layout_type}
            </Typography>
          </Box>
        );
      default:
        return 'Unknown step';
    }
  };

  const isLoading = createCanvasMutation.isPending;
  const error = createCanvasMutation.error;

  return (
    <>
      <BrowserTabTitle>Create Canvas</BrowserTabTitle>
      <PageTitle text="Create Canvas" />
      <Box sx={{ width: '100%' }}>
        <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 4 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
        <Paper elevation={3} sx={{ p: 4, mb: 4 }}>
          {renderStepContent(activeStep)}
        </Paper>
        <Box sx={{ display: 'flex', flexDirection: 'row', pt: 2 }}>
          <Button
            color="inherit"
            disabled={activeStep === 0}
            onClick={handleBack}
            sx={{ mr: 1 }}
            startIcon={<ArrowBackIcon />}
          >
            Back
          </Button>
          <Box sx={{ flex: '1 1 auto' }} />
          <Button onClick={handleCancel} sx={{ mr: 1 }} variant="outlined">
            Cancel
          </Button>
          <Button
            onClick={
              activeStep === steps.length - 1 ? handleCreate : handleNext
            }
            disabled={!isStepValid(activeStep) || isLoading}
            variant="contained"
            endIcon={
              activeStep === steps.length - 1 ? null : <ArrowForwardIcon />
            }
          >
            {activeStep === steps.length - 1 ? (
              isLoading ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                'Create Canvas'
              )
            ) : (
              'Next'
            )}
          </Button>
        </Box>
        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            Error creating canvas: {error.message}
          </Alert>
        )}
      </Box>
    </>
  );
};
